import {
    CheckApplicationInfoResult,
    GarminDeviceStatus,
    GarminSDK,
    IQApp,
    IQDevice,
} from "capacitor-garmin-sdk";
import {
    APP_STORE_CONNECT_IQ_URL,
    APP_STORE_CONNECT_URL,
    GARMIN_BT_MIN_VERSION,
    GARMIN_FULL_APP_INFO,
    GARMIN_INCOMPATIBLE_DEVICES,
    GARMIN_LITE_DEVICES,
    PLAY_STORE_CONNECT_IQ_URL,
    PLAY_STORE_CONNECT_URL,
} from "../../constants/GarminConstants";
import { store } from "../../redux/store";
import {
    setGarminKnownDevices,
    setGarminSelectedDevice,
} from "../../redux/actions/garminActions";
import { SelectedDevice } from "../../redux/models/refsixState";
import { isAndroid, isIos } from "../platformDetection";

const GARMIN_LITE_DEVICES_LOWERCASED = GARMIN_LITE_DEVICES.map((model) =>
    model.toLowerCase()
);

export const isGarminLiteDevice = (model: string) => {
    // do a case-insensitive check
    return GARMIN_LITE_DEVICES_LOWERCASED.includes(model.toLowerCase());
};

export const isGarminBTCompatible = (version: number) => {
    if (version === 0) {
        // This is a development version of the app so assume it is capable
        return true;
    }
    return version >= GARMIN_BT_MIN_VERSION;
};

/**
 * Removes cycle computers and other devices that definetly wouldn't be a watch
 * @param devices ConnectIQDevice
 */
export const filterKnownDevices = (devices: IQDevice[]) => {
    return devices.filter((device) => {
        // filter if the device's name contains a term from the INCOMPATIBLE_DEVICES list
        return !GARMIN_INCOMPATIBLE_DEVICES.some((term) =>
            device.friendlyName.toLowerCase().includes(term)
        );
    });
};

/**
 * Removes watches that are only compatible with the Garmin Lite app
 * @param devices
 */
export const filterLiteDevices = (devices: IQDevice[]) => {
    return devices.filter((device) => {
        return !isGarminLiteDevice(device.friendlyName);
    });
};

export const discoverGarminDevices = async () => {};

export const checkDeviceHasRefsixInstalled = async (device: IQDevice) => {
    let appInfo: CheckApplicationInfoResult | undefined;

    try {
        const info = await GarminSDK.checkApplicationInfo({
            device: device,
            app: GARMIN_FULL_APP_INFO,
        });
        appInfo = info;
    } catch (error) {
        console.error(`Error checking selected watch`, error);
        throw error;
    }

    return appInfo;
};

export type GarminWatchStatus = {
    hasAppInstalled: boolean;
    isLiteDevice: boolean;
    isBTCompatible: boolean;
    watchAppVersion: number;
    isConnected: boolean;
};

/**
 * Check if a given garmin watch has the app installed and is compatible with
 * blutooth messaging
 * @param device
 */
export const checkCorrectGarminAppVersion = async (device: IQDevice) => {
    try {
        // check if the watch has the refsix app installed
        const info = await GarminSDK.checkApplicationInfo({
            device: device,
            app: GARMIN_FULL_APP_INFO,
        });

        return (
            info &&
            info.installed &&
            info.version &&
            isGarminBTCompatible(info?.version)
        );
    } catch (error) {
        console.error(`Error checking selected watch`, error);
        throw error;
    }
};

export const updateGarminKnownDevices = async () => {
    try {
        const { devices } = await GarminSDK.getKnownDevices();
        // iOS will happen seperately via the URL listener
        if (isAndroid()) {
            store.dispatch(setGarminKnownDevices(devices));
        }
    } catch (error) {
        console.error(`Error updating known devices`, error);
    }
};

export const processIosGarminDevices = async (url: string) => {
    let devices = parseGarminUrl(url);
    store.dispatch(setGarminKnownDevices(devices));
};

export const openStoreForConnectMobile = async () => {
    const url = isIos() ? APP_STORE_CONNECT_URL : PLAY_STORE_CONNECT_URL;
    window.location.href = url;
};

export const openStoreForConnectIQ = async () => {
    const url = isIos() ? APP_STORE_CONNECT_IQ_URL : PLAY_STORE_CONNECT_IQ_URL;
    window.location.href = url;
};

export const openConnectMobile = async () => {
    return GarminSDK.openGarminConnect();
};

/**
 * Check if a given garmin watch has the app installed and is compatible with
 * bluetooth messaging. If not show the store to install/update the app
 * @param device
 */
export const checkGarminDeviceReady = async (device: IQDevice) => {
    try {
        if (!(await checkCorrectGarminAppVersion(device))) {
            // prompt to install the app
            await GarminSDK.showConnectIQStoreForApp({
                device,
                app: GARMIN_FULL_APP_INFO,
            });
        }
    } catch (error) {
        console.error(`Error checking selected watch`, error);
        throw error;
    }
};

export const updateSelectedDevice = async (
    device: IQDevice | SelectedDevice,
    app: IQApp = GARMIN_FULL_APP_INFO
) => {
    let status: GarminDeviceStatus | undefined = undefined;
    let appInfo: CheckApplicationInfoResult | undefined;

    // check connection
    try {
        status = await GarminSDK.getDeviceStatus({
            device,
        });
    } catch (error) {
        console.log(`Error checking selected watch`, error);
    }

    if (status?.isConnected) {
        // get app info
        try {
            appInfo = await GarminSDK.checkApplicationInfo({
                device: device,
                app: app,
            });
        } catch (error) {
            console.log(`Error checking selected watch`, error);
        }
    } else {
        console.log(
            `[updateSelectedDevice] not connected - skipping app info check`
        );
    }

    const garminDevice: SelectedDevice = {
        ...device,
        status: status ? status.status : device.status,
        isConnected: status ? status.isConnected : device.isConnected,
        hasAppInstalled: appInfo?.installed,
        isLiteDevice: isGarminLiteDevice(device.friendlyName),
        isBTCompatible:
            appInfo?.version || appInfo?.version === 0
                ? isGarminBTCompatible(appInfo.version)
                : undefined,
        watchAppVersion: appInfo?.version,
    };

    store.dispatch(setGarminSelectedDevice(garminDevice));
    return garminDevice;
};

export const parseGarminUrl = (url: string) => {
    // Create an array to hold devices
    const devicesArray: IQDevice[] = [];

    console.log("Parsing Garmin URL:", JSON.stringify(url));
    // Create a URL object from the string to easily access parameters
    const urlObj = new URL(url);

    // Define the number of devices (Garmin URLs often return multiple devices, here is just 1 as an example)
    let deviceIndex = 0;

    // Loop through the URL search params and extract each device's details
    while (urlObj.searchParams.get(`d${deviceIndex}ID`)) {
        const device: IQDevice = {
            uuid: urlObj.searchParams.get(`d${deviceIndex}ID`) || "",
            friendlyName:
                urlObj.searchParams.get(`d${deviceIndex}Name`) ||
                "Unknown Device",
            modelName:
                urlObj.searchParams.get(`d${deviceIndex}Model`) ||
                "Unknown Model",
        };

        // Add the device object to the devices array
        devicesArray.push(device);

        // Increment to check if more devices exist
        deviceIndex++;
    }

    // Return the array of parsed devices
    return devicesArray;
};
